import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'src/components/ui/use-toast';

/** The message an API error carries (HttpError keeps the server's `message`). */
export function getErrorMessage(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }
  return undefined;
}

/**
 * For actions that aren't forms. Http.setToastError() already toasts every
 * error except 422s (forms render those inline) and cancelled requests, so
 * this shows only the 422 reason - anything more would replace Http's toast.
 */
export function useApiErrorToast() {
  const { toast } = useToast();
  const { t } = useTranslation();

  return useCallback(
    (error: unknown) => {
      if ((error as { code?: unknown } | null)?.code !== 422) {
        return;
      }
      toast({
        variant: 'destructive',
        title: t('Error') || 'Error',
        description: getErrorMessage(error) ?? 'Something went wrong. Please try again.',
      });
    },
    [toast, t],
  );
}
